import { RANKED_CAD_FILE_TYPES } from 'constants/all';

import { getFileExtension, getFileNameWithOutExtension } from '../helpers';

export type FileInfo = {
  file: File;
  units: string;
  thickness: null;
  tempId: string;
};

export type LineItemFileInfo = {
  primaryFileInfo: FileInfo;
  supportingFileInfos: FileInfo[];
};

const isCadFileExtension = (
  value: string,
): value is keyof typeof RANKED_CAD_FILE_TYPES =>
  value in RANKED_CAD_FILE_TYPES;

export const compareFilenamesByType = (a: string, b: string): number => {
  const aExtension: string = getFileExtension(a);
  const aIsACadFile = isCadFileExtension(aExtension);
  const aIsAPdf = aExtension === 'pdf';

  const bExtension: string = getFileExtension(b);
  const bIsACadFile = isCadFileExtension(bExtension);
  const bIsAPdf = bExtension === 'pdf';

  return aIsACadFile
    ? bIsACadFile
      ? RANKED_CAD_FILE_TYPES[aExtension] - RANKED_CAD_FILE_TYPES[bExtension]
      : -1
    : bIsACadFile
    ? 1
    : aIsAPdf
    ? bIsAPdf
      ? 0
      : -1
    : bIsAPdf
    ? 1
    : 0;
};

export const compareFilesByType = (a: FileInfo, b: FileInfo): number => {
  return compareFilenamesByType(a.file.name, b.file.name);
};

export const mapPrimaryFilesToSupportingFiles = (
  fileInfos: FileInfo[],
): LineItemFileInfo[] => {
  const fileInfoMap = new Map<string, LineItemFileInfo>();

  fileInfos.sort(compareFilesByType);

  fileInfos.forEach((fileInfo) => {
    const filenameWithoutExtension = getFileNameWithOutExtension(
      fileInfo.file.name,
    );

    const infoOfPrimaryFileThisFileSupports = fileInfoMap.get(
      filenameWithoutExtension,
    );

    if (infoOfPrimaryFileThisFileSupports) {
      infoOfPrimaryFileThisFileSupports.supportingFileInfos.push(fileInfo);
    } else {
      fileInfoMap.set(filenameWithoutExtension, {
        primaryFileInfo: fileInfo,
        supportingFileInfos: [],
      });
    }
  });

  return Array.from(fileInfoMap.values());
};

export const buildFlatFileInfoMap = (
  fileInfos: FileInfo[],
): LineItemFileInfo[] =>
  fileInfos.map((fileInfo) => ({
    primaryFileInfo: fileInfo,
    supportingFileInfos: [],
  }));

export type BuildFileInfosMapParam = {
  fileInfos: FileInfo[];
  shouldMergeIdenticallyNamedFiles?: boolean;
};

export const buildLineItemsFileInfo = ({
  fileInfos,
  shouldMergeIdenticallyNamedFiles = true,
}: BuildFileInfosMapParam): LineItemFileInfo[] =>
  shouldMergeIdenticallyNamedFiles
    ? mapPrimaryFilesToSupportingFiles(fileInfos)
    : buildFlatFileInfoMap(fileInfos);
